import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import BottomNav from "@/components/BottomNav";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import Papa from "papaparse";
import {
  Upload, ArrowLeft, ArrowRight, FileSpreadsheet, CheckCircle2,
  AlertTriangle, Loader2, Download, ClipboardCheck, Trash2, X, Check
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CERT_TYPES = [
  { id: "pat_testing", label: "PAT Testing", icon: "ClipboardCheck", color: "#8B5CF6", credit: "pat" },
  { id: "minor_works", label: "Minor Works (Electrical)", icon: "Zap", color: "#00A3E0", credit: "electrical" },
  { id: "gas_safe", label: "Gas Safe", icon: "Flame", color: "#EF4444", credit: "gas" },
];

const COLUMNS_BY_TYPE = {
  pat_testing: [
    { key: "client_company", label: "Client / Company", required: true },
    { key: "client_address", label: "Client Address", required: true },
    { key: "client_contact", label: "Contact Name", required: false },
    { key: "client_email", label: "Client Email", required: false },
    { key: "location", label: "Appliance Location", required: false },
    { key: "appliance_id", label: "Appliance ID", required: false },
    { key: "description", label: "Description", required: false },
    { key: "visual", label: "Visual (P/F/S)", required: false },
    { key: "polarity", label: "Polarity (P/F/S)", required: false },
    { key: "earth_ohms", label: "Earth (Ohms)", required: false },
    { key: "insulation_mohms", label: "Insulation (MOhms)", required: false },
    { key: "fail_reason", label: "Fail Reason", required: false },
    { key: "notes", label: "Notes", required: false },
  ],
  minor_works: [
    { key: "client_name", label: "Client Name", required: true },
    { key: "installation_address", label: "Installation Address", required: true },
    { key: "installation_postcode", label: "Postcode", required: false },
    { key: "client_email", label: "Client Email", required: false },
    { key: "description_of_works", label: "Description of Works", required: false },
    { key: "cert_date", label: "Certificate Date", required: false },
    { key: "system_earthing", label: "System Earthing", required: false },
    { key: "circuit_description", label: "Circuit Description", required: false },
  ],
  gas_safe: [
    { key: "client_name", label: "Client Name", required: true },
    { key: "installation_address", label: "Installation Address", required: true },
    { key: "installation_postcode", label: "Postcode", required: false },
    { key: "client_email", label: "Client Email", required: false },
    { key: "description_of_works", label: "Description of Works", required: false },
    { key: "cert_date", label: "Certificate Date", required: false },
    { key: "form_type", label: "Form Type", required: false },
    { key: "form_title", label: "Form Title", required: false },
  ],
};

const STEP_LABELS = ["Upload CSV", "Map Columns", "Preview", "Create"];

function parseCSVText(text) {
  const result = Papa.parse(text.trim(), { header: true, skipEmptyLines: true });
  if (!result.data || result.data.length === 0) return { headers: [], rows: [] };
  return { headers: result.meta.fields || [], rows: result.data };
}

function autoMapColumns(csvHeaders, columns) {
  const mapping = {};
  const normalize = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

  for (const col of columns) {
    const nk = normalize(col.key);
    const nl = normalize(col.label);
    for (const h of csvHeaders) {
      const nh = normalize(h);
      if (nh === nk || nh === nl || nh.includes(nk) || nk.includes(nh)) {
        mapping[col.key] = h;
        break;
      }
    }
    // Fallback heuristics
    if (!mapping[col.key]) {
      const heuristics = {
        client_company: ["company", "client", "site", "business", "firm"],
        client_address: ["address", "addr", "site_address", "location_address"],
        client_contact: ["contact", "name", "person"],
        client_email: ["email", "mail"],
        location: ["location", "room", "area", "floor"],
        appliance_id: ["id", "asset", "tag", "number", "appliance_id"],
        description: ["desc", "appliance", "item", "equipment", "type"],
        visual: ["visual", "vis"],
        polarity: ["polarity", "pol"],
        earth_ohms: ["earth", "continuity", "ohm"],
        insulation_mohms: ["insulation", "insul", "meg", "mohm"],
        fail_reason: ["fail", "reason"],
        notes: ["note", "comment", "remark"],
      };
      for (const hint of (heuristics[col.key] || [])) {
        const match = csvHeaders.find((h) => normalize(h).includes(hint));
        if (match && !Object.values(mapping).includes(match)) {
          mapping[col.key] = match;
          break;
        }
      }
    }
  }
  return mapping;
}

export default function BatchUploadPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [certType, setCertType] = useState("pat_testing");
  const [step, setStep] = useState(0);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [validating, setValidating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [removedGroups, setRemovedGroups] = useState(new Set());

  const columns = COLUMNS_BY_TYPE[certType] || COLUMNS_BY_TYPE.pat_testing;
  const certTypeConfig = CERT_TYPES.find((t) => t.id === certType) || CERT_TYPES[0];

  // Step 0: Upload
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { headers, rows } = parseCSVText(ev.target.result);
      if (rows.length === 0) { toast.error("CSV has no data rows"); return; }
      setCsvHeaders(headers);
      setCsvRows(rows);
      setMapping(autoMapColumns(headers, columns));
      setStep(1);
      toast.success(`Loaded ${rows.length} rows from CSV`);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handlePaste = (text) => {
    const { headers, rows } = parseCSVText(text);
    if (rows.length === 0) { toast.error("CSV has no data rows"); return; }
    setCsvHeaders(headers);
    setCsvRows(rows);
    setMapping(autoMapColumns(headers, columns));
    setStep(1);
    toast.success(`Loaded ${rows.length} rows`);
  };

  // Step 1 → 2: Validate
  const validateAndPreview = async () => {
    const mapped = csvRows.map((row) => {
      const out = {};
      for (const col of columns) {
        const csvCol = mapping[col.key];
        out[col.key] = csvCol ? (row[csvCol] || "") : "";
      }
      return out;
    });

    setValidating(true);
    try {
      const { data } = await axios.post(`${API}/certificates/batch/validate`, {
        rows: mapped, cert_type: certType,
      }, { withCredentials: true });
      setPreview(data);
      setRemovedGroups(new Set());
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Validation failed");
    } finally {
      setValidating(false);
    }
  };

  // Step 2 → 3: Create
  const createBatch = async () => {
    if (!preview) return;
    const certs = preview.certificates.filter((_, i) => !removedGroups.has(i));
    if (certs.length === 0) { toast.error("No certificates to create"); return; }

    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/certificates/batch/create`, {
        certificates: certs,
        cert_type: certType,
        contractor: {
          contractor_name: user?.company || "",
          contractor_email: user?.email || "",
          contractor_phone: "",
          engineer_name: user?.name || "",
        },
      }, { withCredentials: true });
      setResult(data);
      setStep(3);
      toast.success(`${data.total_created} certificates created!`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Batch creation failed");
    } finally {
      setCreating(false);
    }
  };

  const activeCertCount = preview ? preview.certificates.filter((_, i) => !removedGroups.has(i)).length : 0;

  const downloadSampleCSV = () => {
    const csv = `Client Company,Client Address,Client Contact,Client Email,Location,Appliance ID,Description,Visual,Polarity,Earth Ohms,Insulation MOhms
ABC Office Ltd,10 High Street London,John Smith,john@abc.com,Reception,1001,Monitor,P,P,0.05,>199
ABC Office Ltd,10 High Street London,John Smith,john@abc.com,Reception,1002,Kettle,P,P,0.03,>199
ABC Office Ltd,10 High Street London,John Smith,john@abc.com,Server Room,1003,UPS,P,P,0.08,>199
XYZ School,5 Park Road Manchester,Jane Doe,jane@xyz.edu,Classroom 1,2001,Projector,P,P,0.04,>199
XYZ School,5 Park Road Manchester,Jane Doe,jane@xyz.edu,Classroom 1,2002,Laptop,P,P,0.02,>199
XYZ School,5 Park Road Manchester,Jane Doe,jane@xyz.edu,Staff Room,2003,Microwave,F,P,0.01,50
`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "pat_batch_sample.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen pb-16 sm:pb-0" style={{ backgroundColor: 'var(--bg-page)' }} data-testid="batch-upload-page">
      <AppHeader />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/dashboard")} className="p-2 rounded-xl hover:bg-gray-100 transition-colors" style={{ color: 'var(--text-secondary)' }} data-testid="batch-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }} data-testid="batch-title">
              Batch Upload
            </h1>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>Create multiple certificates from a CSV file</p>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-between mb-8" data-testid="batch-steps">
          {STEP_LABELS.map((label, i) => (
            <div key={i} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  step > i ? "bg-[#8B5CF6] text-white" : step === i ? "bg-[#8B5CF6] text-white" : "text-gray-400"
                }`} style={{ backgroundColor: step >= i ? '#8B5CF6' : 'var(--bg-input)', color: step >= i ? 'white' : 'var(--text-muted)' }}>
                  {step > i ? <Check className="w-5 h-5" /> : i + 1}
                </div>
                <span className="text-[10px] mt-1.5 font-semibold text-center hidden sm:block" style={{ fontFamily: 'Outfit, sans-serif', color: step >= i ? 'var(--text-primary)' : 'var(--text-muted)' }}>{label}</span>
              </div>
              {i < STEP_LABELS.length - 1 && <div className="flex-1 h-0.5 mx-2" style={{ backgroundColor: step > i ? '#8B5CF6' : 'var(--border-light)' }} />}
            </div>
          ))}
        </div>

        {/* Step 0: Upload */}
        {step === 0 && (
          <div data-testid="batch-step-upload">
            {/* Certificate Type Selector */}
            <div className="rounded-2xl p-5 mb-4" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
              <h3 className="text-sm font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>Certificate Type</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="batch-type-selector">
                {CERT_TYPES.map((t) => (
                  <button key={t.id} onClick={() => setCertType(t.id)} data-testid={`batch-type-${t.id}`}
                    className={`rounded-xl p-4 text-left transition-all border-2 ${certType === t.id ? '' : 'border-transparent'}`}
                    style={{
                      backgroundColor: certType === t.id ? `${t.color}10` : 'var(--row-alt)',
                      borderColor: certType === t.id ? t.color : 'transparent',
                    }}>
                    <p className="text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: certType === t.id ? t.color : 'var(--text-primary)' }}>{t.label}</p>
                    <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                      {t.id === "pat_testing" ? "Rows grouped by site" : "1 row = 1 certificate"}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-2xl p-8 text-center" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#F3EFFE' }}>
                <FileSpreadsheet className="w-8 h-8 text-[#8B5CF6]" />
              </div>
              <h2 className="text-lg font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>Upload your CSV</h2>
              <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: 'var(--text-secondary)' }}>
                {certType === "pat_testing"
                  ? "Each row should be an appliance. Rows with the same Client Company + Address will be grouped into one PAT certificate."
                  : `Each row will create one ${certTypeConfig.label} certificate.`}
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6">
                <label className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white bg-[#8B5CF6] hover:bg-[#7C3AED] transition-colors cursor-pointer" data-testid="batch-file-upload">
                  <Upload className="w-4 h-4" /> Choose CSV File
                  <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleFile} />
                </label>
                <button onClick={downloadSampleCSV} className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold transition-colors" style={{ backgroundColor: 'var(--row-alt)', color: 'var(--text-secondary)' }} data-testid="batch-download-sample">
                  <Download className="w-4 h-4" /> Download Sample CSV
                </button>
              </div>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t" style={{ borderColor: 'var(--border-light)' }} /></div>
                <div className="relative flex justify-center"><span className="px-3 text-xs font-semibold" style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-muted)' }}>or paste CSV text below</span></div>
              </div>

              <PasteArea onPaste={handlePaste} />
            </div>

            {/* Sample format */}
            <div className="rounded-2xl p-5 mt-4" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
              <h3 className="text-sm font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>Expected CSV Format</h3>
              <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
                {certType === "pat_testing"
                  ? <>Rows with the same <strong>Client Company</strong> + <strong>Client Address</strong> will be grouped into one PAT report. Each group uses 1 PAT credit.</>
                  : <>Each row creates one {certTypeConfig.label} certificate using 1 {certTypeConfig.credit} credit.</>}
              </p>
              <div className="overflow-x-auto">
                <table className="text-[10px] w-full border-collapse">
                  <thead>
                    <tr>
                      {columns.map((c) => (
                        <th key={c.key} className="px-2 py-1.5 text-left font-bold whitespace-nowrap" style={{ backgroundColor: certTypeConfig.color, color: 'white' }}>
                          {c.label} {c.required && <span className="text-yellow-300">*</span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Column Mapping */}
        {step === 1 && (
          <div data-testid="batch-step-mapping">
            <div className="rounded-2xl p-5" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
              <h2 className="text-lg font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>Map Your Columns</h2>
              <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                We auto-detected your CSV columns. Adjust if needed — {csvRows.length} rows loaded.
              </p>

              <div className="space-y-3">
                {columns.map((col) => (
                  <div key={col.key} className="flex items-center gap-3">
                    <label className="text-xs font-semibold w-40 flex-shrink-0" style={{ fontFamily: 'Outfit, sans-serif', color: col.required ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                      {col.label} {col.required && <span className="text-red-500">*</span>}
                    </label>
                    <select
                      data-testid={`mapping-${col.key}`}
                      value={mapping[col.key] || ""}
                      onChange={(e) => setMapping((prev) => ({ ...prev, [col.key]: e.target.value }))}
                      className="flex-1 rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]"
                      style={{ backgroundColor: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-primary)' }}
                    >
                      <option value="">— Skip —</option>
                      {csvHeaders.map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                    {mapping[col.key] && (
                      <span className="text-[10px] flex-shrink-0 px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">
                        <Check className="w-3 h-3 inline -mt-0.5" /> Mapped
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {/* Preview first 3 rows */}
              {csvRows.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-xs font-bold mb-2" style={{ color: 'var(--text-secondary)' }}>Preview (first 3 rows)</h3>
                  <div className="overflow-x-auto">
                    <table className="text-[10px] w-full border-collapse">
                      <thead>
                        <tr>
                          {csvHeaders.map((h) => (
                            <th key={h} className="px-2 py-1.5 text-left font-bold whitespace-nowrap" style={{ backgroundColor: 'var(--border-main)', color: 'white' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvRows.slice(0, 3).map((row, i) => (
                          <tr key={i} style={{ backgroundColor: i % 2 === 0 ? 'var(--bg-card)' : 'var(--row-alt)' }}>
                            {csvHeaders.map((h) => (
                              <td key={h} className="px-2 py-1 truncate max-w-[120px]">{row[h] || ""}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-between mt-6">
              <button onClick={() => { setStep(0); setCsvRows([]); setCsvHeaders([]); }} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors" style={{ backgroundColor: 'var(--row-alt)', color: 'var(--text-secondary)' }} data-testid="batch-map-back">
                <ArrowLeft className="w-4 h-4" /> Re-upload
              </button>
              <button onClick={validateAndPreview} disabled={validating || !mapping[columns.find(c => c.required)?.key]} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#8B5CF6] hover:bg-[#7C3AED] transition-colors disabled:opacity-50" data-testid="batch-map-next">
                {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {validating ? "Validating..." : "Validate & Preview"}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Preview */}
        {step === 2 && preview && (
          <div data-testid="batch-step-preview">
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {[
                { label: "Certificates", value: activeCertCount, color: "#8B5CF6" },
                { label: "Appliances", value: preview.total_appliances, color: "#00A3E0" },
                { label: "Credits Needed", value: activeCertCount, color: activeCertCount <= preview.credits_available ? "#22C55E" : "#EF4444" },
                { label: "Credits Available", value: preview.credits_available, color: "#22C55E" },
              ].map((s) => (
                <div key={s.label} className="rounded-2xl p-4 text-center" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
                  <p className="text-2xl font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace', color: s.color }}>{s.value}</p>
                  <p className="text-[10px] font-semibold mt-1" style={{ color: 'var(--text-secondary)' }}>{s.label}</p>
                </div>
              ))}
            </div>

            {activeCertCount > preview.credits_available && (
              <div className="flex items-center gap-2 rounded-xl p-3 mb-4 bg-red-50 border border-red-200" data-testid="batch-credit-warning">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700">You need {activeCertCount} PAT credits but only have {preview.credits_available}. Remove some certificates or buy more credits.</p>
              </div>
            )}

            {preview.errors.length > 0 && (
              <div className="rounded-xl p-3 mb-4 bg-yellow-50 border border-yellow-200">
                <p className="text-sm font-semibold text-yellow-800 mb-1"><AlertTriangle className="w-4 h-4 inline mr-1" />{preview.errors.length} row(s) skipped</p>
                {preview.errors.slice(0, 5).map((e, i) => (
                  <p key={i} className="text-xs text-yellow-700">Row {e.row}: {e.error}</p>
                ))}
              </div>
            )}

            {/* Certificate groups */}
            <div className="space-y-3" data-testid="batch-preview-list">
              {preview.certificates.map((cert, i) => (
                <div key={i} className={`rounded-2xl p-4 transition-all ${removedGroups.has(i) ? 'opacity-40' : ''}`} style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }} data-testid={`batch-cert-${i}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${certTypeConfig.color}15` }}>
                      <ClipboardCheck className="w-5 h-5" style={{ color: certTypeConfig.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-bold truncate" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>{cert.client_company || cert.client_name || "Unknown"}</p>
                        {cert.appliances && cert.appliances.length > 0 && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${certTypeConfig.color}20`, color: certTypeConfig.color }}>{cert.appliances.length} appliance{cert.appliances.length !== 1 ? "s" : ""}</span>
                        )}
                        {!cert.appliances && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${certTypeConfig.color}20`, color: certTypeConfig.color }}>{certTypeConfig.label}</span>
                        )}
                      </div>
                      <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>{cert.client_address || cert.installation_address}</p>
                      {cert.client_email && <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)', fontFamily: 'IBM Plex Mono, monospace' }}>{cert.client_email}</p>}
                      {cert.description_of_works && <p className="text-[10px] mt-1" style={{ color: 'var(--text-secondary)' }}>{cert.description_of_works}</p>}
                      {/* Appliance summary for PAT */}
                      {cert.appliances && cert.appliances.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {cert.appliances.slice(0, 5).map((a, j) => (
                          <span key={j} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${a.overall === "PASS" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                            {a.appliance_id || a.description || `#${j + 1}`}: {a.overall}
                          </span>
                        ))}
                        {cert.appliances.length > 5 && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--row-alt)', color: 'var(--text-muted)' }}>+{cert.appliances.length - 5} more</span>
                        )}
                      </div>
                      )}
                    </div>
                    <button
                      onClick={() => setRemovedGroups((prev) => {
                        const next = new Set(prev);
                        next.has(i) ? next.delete(i) : next.add(i);
                        return next;
                      })}
                      className="p-2 rounded-xl transition-colors flex-shrink-0"
                      style={{ backgroundColor: removedGroups.has(i) ? '#D1FAE5' : '#FEE2E2', color: removedGroups.has(i) ? '#065F46' : '#991B1B' }}
                      data-testid={`batch-toggle-${i}`}
                    >
                      {removedGroups.has(i) ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-between mt-6">
              <button onClick={() => setStep(1)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors" style={{ backgroundColor: 'var(--row-alt)', color: 'var(--text-secondary)' }} data-testid="batch-preview-back">
                <ArrowLeft className="w-4 h-4" /> Back to Mapping
              </button>
              <button
                onClick={createBatch}
                disabled={creating || activeCertCount === 0 || activeCertCount > preview.credits_available}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#8B5CF6] hover:bg-[#7C3AED] transition-colors disabled:opacity-50"
                data-testid="batch-create-btn"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {creating ? "Creating..." : `Create ${activeCertCount} Certificate${activeCertCount !== 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Result */}
        {step === 3 && result && (
          <div data-testid="batch-step-result">
            <div className="rounded-2xl p-8 text-center" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
              <CheckCircle2 className="w-16 h-16 text-[#22C55E] mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>Batch Complete!</h2>
              <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                {result.total_created} certificate{result.total_created !== 1 ? "s" : ""} created · {result.credits_used} credit{result.credits_used !== 1 ? "s" : ""} used
                {result.total_failed > 0 && <span className="text-red-600"> · {result.total_failed} failed</span>}
              </p>

              {/* Created list */}
              <div className="space-y-2 text-left max-w-lg mx-auto mb-6">
                {result.created.map((c) => (
                  <div key={c.certificate_id} className="flex items-center gap-3 rounded-xl p-3" style={{ backgroundColor: 'var(--row-alt)' }}>
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{c.certificate_id}</p>
                      <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>{c.client_company} — {c.appliance_count} appliance{c.appliance_count !== 1 ? "s" : ""}</p>
                    </div>
                  </div>
                ))}
              </div>

              {result.failed.length > 0 && (
                <div className="space-y-2 text-left max-w-lg mx-auto mb-6">
                  <h3 className="text-sm font-bold text-red-600">Failed</h3>
                  {result.failed.map((f, i) => (
                    <div key={i} className="flex items-center gap-3 rounded-xl p-3 bg-red-50">
                      <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold">{f.client_company}</p>
                        <p className="text-xs text-red-600">{f.error}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button onClick={() => navigate("/dashboard")} className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white bg-[#8B5CF6] hover:bg-[#7C3AED] transition-colors" data-testid="batch-go-dashboard">
                  View in Dashboard
                </button>
                <button onClick={() => { setStep(0); setCsvRows([]); setCsvHeaders([]); setPreview(null); setResult(null); }} className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold transition-colors" style={{ backgroundColor: 'var(--row-alt)', color: 'var(--text-secondary)' }} data-testid="batch-upload-another">
                  <Upload className="w-4 h-4" /> Upload Another
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
}

function PasteArea({ onPaste }) {
  const [text, setText] = useState("");
  return (
    <div className="text-left">
      <textarea
        data-testid="batch-paste-area"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
        placeholder={"Client Company,Client Address,Location,Appliance ID,Description,Visual,Polarity,Earth Ohms,Insulation MOhms\nABC Office,10 High St,Reception,1001,Monitor,P,P,0.05,>199"}
        className="w-full rounded-xl border px-3 py-2.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] resize-none"
        style={{ backgroundColor: 'var(--bg-input)', borderColor: 'var(--border-main)', color: 'var(--text-primary)' }}
      />
      <button
        onClick={() => { if (text.trim()) onPaste(text); }}
        disabled={!text.trim()}
        className="mt-2 flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-[#8B5CF6] hover:bg-[#7C3AED] transition-colors disabled:opacity-50"
        data-testid="batch-paste-import"
      >
        <Upload className="w-3.5 h-3.5" /> Import from Text
      </button>
    </div>
  );
}
